import { useCallback, useEffect, useRef, useState } from 'react'
import { Eraser, Send, Undo2, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { stamp, useTicker } from '../api'
import { appendPoint, blanks, BRUSH, INK, PAPER, seatStyle, SKETCH, splitStroke, type Stroke } from '../helpers'
import type { BoardProps } from './types'

interface DrawView {
  i: number
  total: number
  phase: 'draw' | 'reveal' | 'done'
  drawer: number
  deadline: number
  revealUntil: number
  word: string | null
  hint: number
  ver: number
  strokes: Stroke[] | null
  got: boolean[]
  feed: { seat: number; text: string | null; ok: boolean }[]
  scores: number[]
  gain: number[] | null
}

const DRAW_MS = 80_000
/** How often a line still being drawn is sent, so the others see it grow rather than appear. */
const FLUSH_MS = 700

function paint(canvas: HTMLCanvasElement, strokes: readonly Stroke[]) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.fillStyle = PAPER
  ctx.fillRect(0, 0, SKETCH, SKETCH)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  for (const s of strokes) {
    const color = INK[s.c] ?? INK[0]
    const width = BRUSH[s.w] ?? BRUSH[1]
    ctx.strokeStyle = color
    ctx.fillStyle = color
    ctx.lineWidth = width
    if (s.p.length === 2) {
      ctx.beginPath()
      ctx.arc(s.p[0]!, s.p[1]!, width / 2, 0, Math.PI * 2)
      ctx.fill()
      continue
    }
    ctx.beginPath()
    ctx.moveTo(s.p[0]!, s.p[1]!)
    for (let k = 2; k < s.p.length; k += 2) ctx.lineTo(s.p[k]!, s.p[k + 1]!)
    ctx.stroke()
  }
}

function Paper({ strokes, onDown, onMove, onUp, active }: { strokes: readonly Stroke[]; onDown?: (x: number, y: number) => void; onMove?: (x: number, y: number) => void; onUp?: () => void; active: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    if (ref.current) paint(ref.current, strokes)
  }, [strokes])
  const at = (e: React.PointerEvent<HTMLCanvasElement>): [number, number] => {
    const box = e.currentTarget.getBoundingClientRect()
    return [Math.min(SKETCH, Math.max(0, ((e.clientX - box.left) / box.width) * SKETCH)), Math.min(SKETCH, Math.max(0, ((e.clientY - box.top) / box.height) * SKETCH))]
  }
  return (
    <canvas
      ref={ref}
      width={SKETCH}
      height={SKETCH}
      className={cn('mx-auto aspect-square w-full max-w-[min(100%,58vh)] touch-none rounded-xl border border-border bg-card', active ? 'cursor-crosshair' : 'cursor-default')}
      aria-label="The drawing"
      onPointerDown={(e) => {
        if (!active) return
        e.currentTarget.setPointerCapture(e.pointerId)
        onDown?.(...at(e))
      }}
      onPointerMove={(e) => {
        if (active && e.buttons) onMove?.(...at(e))
      }}
      onPointerUp={() => active && onUp?.()}
      onPointerCancel={() => active && onUp?.()}
    />
  )
}

/** The drawer's canvas. What they draw shows at once; it is sent in pieces as they go. */
function DrawerPad({ view, busy, send }: { view: DrawView; busy: boolean; send: BoardProps<DrawView>['send'] }) {
  const [mine, setMine] = useState<Stroke[]>(view.strokes ?? [])
  const [live, setLive] = useState<Stroke | null>(null)
  const [color, setColor] = useState(0)
  const [size, setSize] = useState(1)
  const unsent = useRef<Stroke[]>([])
  const drawing = useRef<Stroke | null>(null)
  const lastFlush = useRef(0)
  const round = useRef(view.i)

  // A new round starts a clean page.
  useEffect(() => {
    if (round.current !== view.i) {
      round.current = view.i
      unsent.current = []
      drawing.current = null
      setMine([])
      setLive(null)
    }
  }, [view.i])

  const flush = useCallback(() => {
    if (!unsent.current.length) return
    const batch = unsent.current.flatMap((s) => splitStroke(s)).slice(0, 40)
    unsent.current = []
    send({ strokes: batch })
  }, [send])

  const start = (x: number, y: number) => {
    drawing.current = { c: color, w: size, p: [Math.round(x), Math.round(y)] }
    lastFlush.current = stamp()
    setLive(drawing.current)
  }
  const move = (x: number, y: number) => {
    const d = drawing.current
    if (!d) return
    const p = appendPoint(d.p, x, y)
    if (p === d.p) return
    d.p = p
    setLive({ ...d })
    // A long line goes out in pieces that share a joint, so the others watch it grow.
    if (stamp() - lastFlush.current > FLUSH_MS && d.p.length >= 4) {
      const done: Stroke = { ...d }
      unsent.current.push(done)
      setMine((m) => [...m, done])
      drawing.current = { c: d.c, w: d.w, p: d.p.slice(-2) }
      setLive(null)
      lastFlush.current = stamp()
      flush()
    }
  }
  const end = () => {
    const d = drawing.current
    drawing.current = null
    setLive(null)
    if (!d) return
    unsent.current.push(d)
    setMine((m) => [...m, d])
    flush()
  }
  const undo = () => {
    flush()
    setMine((m) => m.slice(0, -1))
    send({ undo: true })
  }
  const clear = () => {
    unsent.current = []
    setMine([])
    send({ clear: true })
  }

  const shown = live ? [...mine, live] : mine
  return (
    <div className="grid gap-2">
      <Paper strokes={shown} active={view.phase === 'draw'} onDown={start} onMove={move} onUp={end} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Colour">
          {INK.map((c, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setColor(i)}
              aria-label={i === 8 ? 'Eraser' : `Colour ${i + 1}`}
              aria-pressed={color === i}
              className={cn('grid size-7 place-items-center rounded-full border border-border-strong', color === i && 'ring-2 ring-brand ring-offset-1')}
              style={{ backgroundColor: c }}
            >
              {i === 8 ? <Eraser className="size-3.5 text-text-secondary" /> : null}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1" role="group" aria-label="Brush size">
          {BRUSH.map((_b, i) => (
            <button key={i} type="button" onClick={() => setSize(i)} aria-pressed={size === i} aria-label={`Brush ${i + 1}`} className={cn('grid size-8 place-items-center rounded-md border border-border', size === i && 'border-brand bg-brand-soft')}>
              <span className="rounded-full bg-foreground" style={{ width: 4 + i * 4, height: 4 + i * 4 }} />
            </button>
          ))}
          <Button type="button" size="icon" variant="outline" className="size-8" onClick={undo} disabled={busy || mine.length === 0} aria-label="Undo the last line">
            <Undo2 className="size-4" />
          </Button>
          <Button type="button" size="icon" variant="outline" className="size-8" onClick={clear} disabled={busy || mine.length === 0} aria-label="Clear the page">
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export function Drawing({ view, seat, over, busy, send, skew, players, people }: BoardProps<DrawView>) {
  const now = useTicker(250) + skew
  const [guess, setGuess] = useState('')
  const name = (s: number) => (s === seat ? 'You' : (people.get(players[s] ?? '')?.displayName.split(' ')[0] ?? 'Player'))
  const drawing = view.phase === 'draw' && !over
  const iDraw = seat !== null && seat === view.drawer
  const left = Math.max(0, view.deadline - now)
  const mayGuess = drawing && seat !== null && !iDraw && !view.got[seat]
  const order = view.scores.map((sc, s) => ({ sc, s })).sort((a, b) => b.sc - a.sc)
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_16rem]">
      <div className="grid content-start gap-2">
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="text-xs text-text-muted">
            Round {Math.min(view.i + 1, view.total)} of {view.total}
          </span>
          <span className="min-w-0 flex-1 text-center font-semibold tracking-wide">
            {view.phase !== 'draw' || iDraw || (seat !== null && view.got[seat]) ? (
              <span className={cn(view.phase !== 'draw' && 'text-brand')}>{view.word ?? ''}</span>
            ) : (
              <span className="font-mono">{blanks(view.hint)}</span>
            )}
          </span>
          {drawing ? <span className={cn('num w-8 text-right text-xs tabular-nums', left < 15_000 && 'text-destructive')}>{Math.ceil(left / 1000)}s</span> : <span className="w-8" />}
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
          <div className="h-full bg-brand transition-[width] duration-200 ease-linear" style={{ width: drawing ? `${(left / DRAW_MS) * 100}%` : '0%' }} />
        </div>
        {iDraw && drawing ? <DrawerPad key={view.i} view={view} busy={busy} send={send} /> : <Paper strokes={view.strokes ?? []} active={false} />}
        <p className="text-center text-xs text-text-secondary" aria-live="polite">
          {drawing ? (iDraw ? `Draw “${view.word}” without writing it. The others are guessing.` : `${name(view.drawer)} is drawing.`) : view.phase === 'reveal' ? `It was “${view.word}”.` : ''}
        </p>
      </div>
      <div className="grid content-start gap-3">
        <ol className="grid gap-1" aria-label="Scores">
          {order.map(({ sc, s }) => (
            <li key={s} className={cn('flex items-center gap-2 rounded-md px-2.5 py-1 text-sm', s === view.drawer && drawing ? cn(seatStyle(s).soft) : 'bg-muted')}>
              <span className={cn('size-2.5 rounded-full', seatStyle(s).bg)} />
              <span className="min-w-0 flex-1 truncate font-medium">{name(s)}</span>
              {s === view.drawer && drawing ? <span className="text-xs">✏️</span> : view.got[s] && drawing ? <span className="text-xs text-success">✓</span> : null}
              {view.gain?.[s] ? <span className="num text-xs font-medium text-success tabular-nums">+{view.gain[s]}</span> : null}
              <span className="num w-10 text-right font-semibold tabular-nums">{sc}</span>
            </li>
          ))}
        </ol>
        <ul className="grid max-h-40 gap-1 overflow-y-auto rounded-md border border-border-soft bg-card p-2 text-xs" aria-label="Guesses">
          {view.feed.length === 0 ? <li className="text-text-muted">Guesses show up here.</li> : null}
          {view.feed.map((f, i) => (
            <li key={i} className={cn(f.ok && 'font-semibold text-success')}>
              <span className={seatStyle(f.seat).text}>{name(f.seat)}</span>: {f.ok ? 'got it!' : f.text}
            </li>
          ))}
        </ul>
        {mayGuess ? (
          <form
            className="flex items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault()
              if (guess.trim()) {
                send({ guess: guess.trim() })
                setGuess('')
              }
            }}
          >
            <Input value={guess} onChange={(e) => setGuess(e.target.value)} placeholder="Your guess…" aria-label="Your guess" maxLength={40} autoFocus />
            <Button type="submit" size="icon" aria-label="Send guess" disabled={!guess.trim()}>
              <Send className="size-4" />
            </Button>
          </form>
        ) : null}
      </div>
    </div>
  )
}
